(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parsePlaceholders = (header) => {
    const source = header.querySelector('[data-placeholder-source]');
    if (!source) return [];

    try {
      const value = JSON.parse(source.textContent || '""');
      return String(value)
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const startTypingPlaceholder = (input, placeholders) => {
    if (!input || placeholders.length === 0) return;

    if (reduceMotion || placeholders.length === 1) {
      input.setAttribute('placeholder', placeholders[0]);
      return;
    }

    let phraseIndex = 0;
    let characterIndex = 0;
    let deleting = false;
    let timeoutId;

    const typeNext = () => {
      const phrase = placeholders[phraseIndex];
      input.setAttribute('placeholder', phrase.slice(0, characterIndex));

      if (!deleting && characterIndex < phrase.length) {
        characterIndex += 1;
        timeoutId = window.setTimeout(typeNext, 55);
        return;
      }

      if (!deleting && characterIndex === phrase.length) {
        deleting = true;
        timeoutId = window.setTimeout(typeNext, 950);
        return;
      }

      if (deleting && characterIndex > 0) {
        characterIndex -= 1;
        timeoutId = window.setTimeout(typeNext, 32);
        return;
      }

      deleting = false;
      phraseIndex = (phraseIndex + 1) % placeholders.length;
      timeoutId = window.setTimeout(typeNext, 220);
    };

    typeNext();

    document.addEventListener('shopify:section:unload', () => {
      window.clearTimeout(timeoutId);
    });
  };

  const setMobileMenu = (header, open) => {
    const toggle = header.querySelector('[data-menu-toggle]');
    const menu = header.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('kb-header-menu-open', open);

    if (open) {
      const focusTarget = menu.querySelector('button, a');
      focusTarget?.focus({ preventScroll: true });
    } else {
      toggle.focus({ preventScroll: true });
    }
  };

  const initHeader = (header) => {
    if (header.dataset.premiumHeaderInitialized === 'true') return;
    header.dataset.premiumHeaderInitialized = 'true';

    const input = header.querySelector('[data-typing-search]');
    const placeholders = parsePlaceholders(header);
    if (input && placeholders.length > 0) {
      input.setAttribute('placeholder', placeholders[0]);
      const startTyping = () => startTypingPlaceholder(input, placeholders);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(startTyping, { timeout: 3000 });
      } else {
        setTimeout(startTyping, 2500);
      }
    }

    header.querySelector('[data-search-clear]')?.addEventListener('click', () => {
      if (!input) return;
      input.value = '';
      input.focus();
    });
    
    header.querySelector('[data-menu-toggle]')?.addEventListener('click', () => {
      const menu = header.querySelector('[data-mobile-menu]');
      setMobileMenu(header, Boolean(menu?.hidden));
    });

    header.querySelectorAll('[data-menu-close]').forEach((closeControl) => {
      closeControl.addEventListener('click', () => setMobileMenu(header, false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const menu = header.querySelector('[data-mobile-menu]');
        if (menu && !menu.hidden) setMobileMenu(header, false);
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const menu = header.querySelector('[data-mobile-menu]');
      if (menu && menu.contains(event.target)) {
        setMobileMenu(header, true);
      } else {
        setMobileMenu(header, false);
      }
    });

    document.addEventListener('shopify:block:deselect', () => {
      setMobileMenu(header, false);
    });

    const isJudgeMeModalActive = () => {
      return document.body.classList.contains('jdgm-review-modal-active') || document.documentElement.classList.contains('jdgm-review-modal-active');
    };

    let scrollStopTimeout;
    let cachedStickyHeight = 0;

    const updateStickyHeight = () => {
      const topSticky = document.querySelector('[data-header-top-sticky], .kb-header-top-sticky');
      if (topSticky && topSticky.offsetHeight > 0) {
        cachedStickyHeight = topSticky.offsetHeight;
      }
    };
    window.addEventListener('resize', updateStickyHeight, { passive: true });

    const syncTopHeaderSticky = () => {
      const topSticky = document.querySelector('[data-header-top-sticky], .kb-header-top-sticky');
      const placeholder = document.querySelector('[data-header-top-placeholder]');

      if (isJudgeMeModalActive()) {
        document.body.classList.add('jdgm-review-modal-active');
        document.documentElement.classList.add('jdgm-review-modal-active');
        return;
      } else {
        document.body.classList.remove('jdgm-review-modal-active');
        document.documentElement.classList.remove('jdgm-review-modal-active');
      }

      if (!topSticky) return;

      if (!cachedStickyHeight && topSticky.offsetHeight) {
        cachedStickyHeight = topSticky.offsetHeight;
      }

      const currentScrollY = window.scrollY;

      if (currentScrollY > 10) {
        if (!topSticky.classList.contains('kb-header-top--fixed')) {
          topSticky.classList.add('kb-header-top--fixed');
        }
        topSticky.classList.remove('kb-header--hidden');
        if (placeholder) {
          placeholder.style.display = 'block';
          placeholder.style.height = `${cachedStickyHeight || 60}px`;
        }
      } else {
        topSticky.classList.remove('kb-header-top--fixed');
        topSticky.classList.remove('kb-header--hidden');
        if (placeholder) {
          placeholder.style.display = 'none';
          placeholder.style.height = '0px';
        }
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          syncTopHeaderSticky();
          ticking = false;
        });
        ticking = true;
      }
    };

    syncTopHeaderSticky();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  const initAllHeaders = () => {
    document.querySelectorAll('[data-premium-header]').forEach(initHeader);
  };

  const formatMoney = (value) => {
    const amount = Number(value || 0) / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'INR' }).format(amount);
  };

  const updateDrawer = (cart) => {
    if (!cart) return;

    if (typeof cart.item_count !== 'undefined') {
      document.querySelectorAll('[data-cart-count], .kb-header__badge--cart, .kb-header__action--cart .kb-header__badge').forEach((badge) => {
        badge.textContent = String(cart.item_count);
      });
    }

    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const title = drawer.querySelector('#CartDrawerTitle');
    if (title) title.textContent = `Your cart (${cart.item_count})`;

    const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  };

  const syncCartState = async () => {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js?_t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      updateDrawer(cart);
    } catch (error) {
      console.error(error);
    }
  };

  const openCartDrawer = async () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const trigger = document.querySelector('[data-cart-drawer-trigger]');
    if (!drawer) return;

    drawer.dataset.cartDrawerMode = 'full';
    drawer.hidden = false;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('kb-cart-drawer-open');
    trigger?.classList.add('is-open');
    await syncCartState();
  };

  const closeCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const trigger = document.querySelector('[data-cart-drawer-trigger]');
    if (!drawer) return;

    drawer.hidden = true;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('kb-cart-drawer-open');
    trigger?.classList.remove('is-open');
    syncCartState();
  };

  document.addEventListener('kb:cart:updated', (event) => {
    if (event.detail?.cart) updateDrawer(event.detail.cart);
    else syncCartState();
  });

  // Automatically sync header cart badge on load and back/forward navigation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCartState);
  } else {
    syncCartState();
  }
  window.addEventListener('pageshow', syncCartState);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-drawer-trigger]')) return;
    event.preventDefault();
    openCartDrawer();
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-drawer-close]')) return;
    closeCartDrawer();
  });

  // Trigger KwikPass Phone OTP Login Popup when account/login button is clicked
  const triggerKwikpassLogin = (evt) => {
    // 1. Direct KwikPass SDK Instance Call
    if (window.KP_LOGIN_SDK_INSTANCE && typeof window.KP_LOGIN_SDK_INSTANCE.handleKpLogin === 'function') {
      window.KP_LOGIN_SDK_INSTANCE.handleKpLogin();
      return true;
    }
    // 2. Global GoKwik / KwikPass method
    if (typeof window.handleKpLogin === 'function') {
      window.handleKpLogin();
      return true;
    }
    // 3. Injected KwikPass button triggers
    const kpBtn = document.querySelector('.kp-login-btn, #kp-login-btn, [data-kp-login], .kwikpass-login-btn, #gokwik-login-btn, [onclick*="handleKpLogin"]');
    if (kpBtn && kpBtn !== evt?.target) {
      kpBtn.click();
      return true;
    }
    return false;
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-account-login-trigger]');
    if (!trigger) return;

    if (triggerKwikpassLogin(event)) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  // Monitor Judge.me Write a Review Modal to Hide Header and Sticky Cart Bar
  const handleJudgeMeModal = () => {
    const overlay = document.getElementById('JdgmBlurOverlay');
    const isOverlayOpen = overlay && window.getComputedStyle(overlay).display !== 'none' && overlay.offsetWidth > 0;
    const isBtnExpanded = !!document.querySelector('.jdgm-write-rev-btn[aria-expanded="true"]');
    const activeForm = document.querySelector('.jdgm-form-wrapper:not([style*="display: none"]), .jdgm-rev-widg__form-wrapper:not([style*="display: none"])');
    const isFormOpen = activeForm && activeForm.offsetHeight > 100;
    const isParamOpen = window.location.search.indexOf('pb=0') !== -1 || window.location.search.indexOf('write') !== -1;

    const shouldHide = isOverlayOpen || isBtnExpanded || isFormOpen || isParamOpen;

    if (shouldHide) {
      document.documentElement.classList.add('jdgm-review-modal-active');
      document.body.classList.add('jdgm-review-modal-active');
    } else {
      document.documentElement.classList.remove('jdgm-review-modal-active');
      document.body.classList.remove('jdgm-review-modal-active');
    }
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('.jdgm-write-rev-btn, .jdgm-btn, .jdgm-all-reviews-rating-actions__write-btn')) {
      setTimeout(handleJudgeMeModal, 10);
      setTimeout(handleJudgeMeModal, 100);
      setTimeout(handleJudgeMeModal, 300);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllHeaders();
      syncCartState();
      handleJudgeMeModal();
    });
  } else {
    initAllHeaders();
    syncCartState();
    handleJudgeMeModal();
  }

  document.addEventListener('shopify:section:load', initAllHeaders);
})();
